import React, { useState, useRef, useEffect } from 'react';
import { Button, Form, Alert, ProgressBar, Card, Row, Col, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCloudUploadAlt, 
  faFile, 
  faFileImage, 
  faFilePdf, 
  faTrashAlt,
  faEye,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import { api } from '../../config/api';

const FileUpload = ({ expenseId, onFilesUploaded, existingFiles = [] }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [existingFilesList, setExistingFilesList] = useState(existingFiles);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [fileToEdit, setFileToEdit] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const fileInputRef = useRef(null);

  // Load existing files when component mounts or expenseId changes
  useEffect(() => {
    const loadExistingFiles = async () => {
      try {
        const response = await api.get(`/files/expense/${expenseId}`);
        if (response.data.success) {
          setExistingFilesList(response.data.data);
          if (onFilesUploaded) {
            onFilesUploaded(response.data.data);
          }
        }
      } catch (err) {
        console.error('Failed to load existing files:', err);
      }
    };

    if (expenseId) {
      loadExistingFiles();
    }
  }, [expenseId, onFilesUploaded]);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    
    // Validate file types and sizes
    const validFiles = selectedFiles.filter(file => {
      const isValidType = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        'application/pdf'
      ].includes(file.type);
      
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
      
      if (!isValidType) {
        setError(`Invalid file type: ${file.name}. Only images and PDFs are allowed.`);
        return false;
      }
      
      if (!isValidSize) {
        setError(`File too large: ${file.name}. Maximum size is 10MB.`);
        return false;
      }
      
      return true;
    });
    
    if (validFiles.length > 0) {
      setError(null);
      setFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    // Add title and description if provided
    if (uploadTitle.trim()) {
      formData.append('title', uploadTitle.trim());
    }
    if (uploadDescription.trim()) {
      formData.append('description', uploadDescription.trim());
    }

    try {
      const response = await api.post(`/files/upload/${expenseId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (response.data.success) {
        setFiles([]);
        setUploadProgress(0);
        setUploadTitle('');
        setUploadDescription('');
        // Refresh the existing files list
        const refreshResponse = await api.get(`/files/expense/${expenseId}`);
        if (refreshResponse.data.success) {
          setExistingFilesList(refreshResponse.data.data);
          if (onFilesUploaded) {
            onFilesUploaded(refreshResponse.data.data);
          }
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const previewFile = async (fileId, mimeType) => {
    try {
      const response = await api.get(`/files/preview/${fileId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      if (mimeType.startsWith('image/')) {
        // For images, open in a new tab
        window.open(url, '_blank');
      } else if (mimeType === 'application/pdf') {
        // For PDFs, open in a new tab
        window.open(url, '_blank');
      }
      
      // Clean up the URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Preview error:', err);
      setError('Failed to preview file');
    }
  };

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;

    try {
      await api.delete(`/files/${fileToDelete.id}`);
      // Refresh the files list
      const response = await api.get(`/files/expense/${expenseId}`);
      if (response.data.success) {
        setExistingFilesList(response.data.data);
        if (onFilesUploaded) {
          onFilesUploaded(response.data.data);
        }
      }
      setShowDeleteModal(false);
      setFileToDelete(null);
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete file');
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setFileToDelete(null);
  };

  const handleEditClick = (file) => {
    setFileToEdit(file);
    setEditTitle(file.title || '');
    setEditDescription(file.description || '');
    setShowEditModal(true);
  };

  const confirmEdit = async () => {
    if (!fileToEdit) return;

    try {
      const response = await api.put(`/files/${fileToEdit.id}`, {
        title: editTitle.trim() || null,
        description: editDescription.trim() || null
      });

      if (response.data.success) {
        // Refresh the files list
        const refreshResponse = await api.get(`/files/expense/${expenseId}`);
        if (refreshResponse.data.success) {
          setExistingFilesList(refreshResponse.data.data);
          if (onFilesUploaded) {
            onFilesUploaded(refreshResponse.data.data);
          }
        }
        setShowEditModal(false);
        setFileToEdit(null);
        setEditTitle('');
        setEditDescription('');
      }
    } catch (err) {
      console.error('Edit error:', err);
      setError('Failed to update file metadata');
    }
  };

  const cancelEdit = () => {
    setShowEditModal(false);
    setFileToEdit(null);
    setEditTitle('');
    setEditDescription('');
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) {
      return faFileImage;
    } else if (mimeType === 'application/pdf') {
      return faFilePdf;
    }
    return faFile;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="file-upload">
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {/* File Upload Section */}
      <Card className="mb-3">
        <Card.Body>
          <h6 className="mb-3">Upload Receipts & Documents</h6>
          
          <Form.Group className="mb-3">
            <Form.Label>Select Files</Form.Label>
            <div className="d-grid">
              <Button
                variant="outline-primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="d-flex align-items-center justify-content-center gap-2"
              >
                <FontAwesomeIcon icon={faCloudUploadAlt} />
                Choose Files (Images & PDFs, max 10MB each)
              </Button>
            </div>
            <Form.Control
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
                         <Form.Text className="text-muted">
               Supported formats: JPEG, PNG, GIF, WebP, PDF. Maximum 10 files per expense.
             </Form.Text>
           </Form.Group>

           {/* Optional Title and Description */}
           <Row>
             <Col md={6}>
               <Form.Group className="mb-3">
                 <Form.Label>Title (Optional)</Form.Label>
                 <Form.Control
                   type="text"
                   placeholder="Enter a title for the files"
                   value={uploadTitle}
                   onChange={(e) => setUploadTitle(e.target.value)}
                   disabled={uploading}
                 />
                 <Form.Text className="text-muted">
                   A custom title to display instead of the filename
                 </Form.Text>
               </Form.Group>
             </Col>
             <Col md={6}>
               <Form.Group className="mb-3">
                 <Form.Label>Description (Optional)</Form.Label>
                 <Form.Control
                   as="textarea"
                   rows={2}
                   placeholder="Enter a description"
                   value={uploadDescription}
                   onChange={(e) => setUploadDescription(e.target.value)}
                   disabled={uploading}
                 />
                 <Form.Text className="text-muted">
                   Additional details about the files
                 </Form.Text>
               </Form.Group>
             </Col>
           </Row>

          {/* Selected Files */}
          {files.length > 0 && (
            <div className="mb-3">
              <h6>Selected Files:</h6>
              {files.map((file, index) => (
                <div key={index} className="d-flex align-items-center justify-content-between p-2 border rounded mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={getFileIcon(file.type)} />
                    <span>{file.name}</span>
                    <small className="text-muted">({formatFileSize(file.size)})</small>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeFile(index)}
                    disabled={uploading}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </Button>
                </div>
              ))}
              
              <Button
                variant="primary"
                onClick={uploadFiles}
                disabled={uploading || files.length === 0}
                className="w-100"
              >
                {uploading ? 'Uploading...' : `Upload ${files.length} File(s)`}
              </Button>
              
              {uploading && (
                <ProgressBar 
                  now={uploadProgress} 
                  label={`${uploadProgress}%`}
                  className="mt-2"
                />
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Existing Files */}
      {existingFilesList.length > 0 && (
        <Card>
          <Card.Body>
            <h6 className="mb-3">Uploaded Files</h6>
            <Row>
              {existingFilesList.map((file) => (
                <Col key={file.id} xs={12} sm={6} md={4} className="mb-3">
                  <Card className="h-100">
                    <Card.Body className="p-3">
                                             <div className="d-flex align-items-center justify-content-between mb-2">
                         <FontAwesomeIcon 
                           icon={getFileIcon(file.mimeType)} 
                           className="text-primary"
                           size="lg"
                         />
                                                   <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => previewFile(file.id, file.mimeType)}
                              title="View file"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleEditClick(file)}
                              title="Edit metadata"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteClick(file)}
                              title="Delete file"
                            >
                              <FontAwesomeIcon icon={faTrashAlt} />
                            </Button>
                          </div>
                       </div>
                      
                                             <div className="text-center">
                         <div className="fw-bold text-truncate" title={file.title || file.originalName}>
                           {file.title || file.originalName}
                         </div>
                         {file.title && (
                           <small className="text-muted d-block">
                             {file.originalName}
                           </small>
                         )}
                        <small className="text-muted">
                          {formatFileSize(file.fileSize)}
                        </small>
                        <br />
                        <small className="text-muted">
                          Uploaded by {file.uploadedBy?.displayName || 'Unknown'}
                        </small>
                        <br />
                                                 <small className="text-muted">
                           {new Date(file.createdAt).toLocaleString()}
                         </small>
                         {file.description && (
                           <small className="text-muted d-block mt-1">
                             {file.description}
                           </small>
                         )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
                     </Card.Body>
         </Card>
       )}

       {/* Delete Confirmation Modal */}
       <Modal show={showDeleteModal} onHide={cancelDelete} centered>
         <Modal.Header closeButton>
           <Modal.Title>Delete File</Modal.Title>
         </Modal.Header>
         <Modal.Body>
           <p>Are you sure you want to delete <strong>{fileToDelete?.originalName}</strong>?</p>
           <p className="text-muted small mb-0">
             This will remove the file from the expense but keep it on the server for backup purposes.
           </p>
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={cancelDelete}>
             Cancel
           </Button>
           <Button variant="danger" onClick={confirmDelete}>
             Delete File
           </Button>
         </Modal.Footer>
       </Modal>

       {/* Edit Metadata Modal */}
       <Modal show={showEditModal} onHide={cancelEdit} centered>
         <Modal.Header closeButton>
           <Modal.Title>Edit File Metadata</Modal.Title>
         </Modal.Header>
         <Modal.Body>
           <Form>
             <Form.Group className="mb-3">
               <Form.Label>Title</Form.Label>
               <Form.Control
                 type="text"
                 placeholder="Enter a title for the file"
                 value={editTitle}
                 onChange={(e) => setEditTitle(e.target.value)}
               />
               <Form.Text className="text-muted">
                 Leave empty to use the original filename
               </Form.Text>
             </Form.Group>
             <Form.Group className="mb-3">
               <Form.Label>Description</Form.Label>
               <Form.Control
                 as="textarea"
                 rows={3}
                 placeholder="Enter a description"
                 value={editDescription}
                 onChange={(e) => setEditDescription(e.target.value)}
               />
               <Form.Text className="text-muted">
                 Additional details about the file
               </Form.Text>
             </Form.Group>
           </Form>
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={cancelEdit}>
             Cancel
           </Button>
           <Button variant="primary" onClick={confirmEdit}>
             Save Changes
           </Button>
         </Modal.Footer>
       </Modal>
     </div>
   );
 };

export default FileUpload;
